import type {
  AdminAuditResponse,
  AdminCaseQueueResponse,
  AdminRoundsResponse,
  AuthenticatedAdmin,
  CasinoProfitResponse,
  PendingAction,
} from "@utopia-well-done-ltd/contracts";
import { useEffect, useState } from "react";
import {
  ApiError,
  api,
  type BlackjackOperationsResponse,
  type PokerOperationsResponse,
} from "../api/client.js";
import { blackjackAlerts } from "../blackjack-health.js";
import { can } from "../capabilities.js";
import { asAmount, asMoment } from "../format.js";
import { pokerAlerts } from "../poker-health.js";

interface DashboardData {
  actions: PendingAction[] | null;
  audit: AdminAuditResponse | null;
  blackjack: BlackjackOperationsResponse | null;
  cases: AdminCaseQueueResponse | null;
  openRounds: AdminRoundsResponse | null;
  poker: PokerOperationsResponse | null;
  profit: CasinoProfitResponse | null;
}

const EMPTY: DashboardData = {
  actions: null,
  audit: null,
  blackjack: null,
  cases: null,
  openRounds: null,
  poker: null,
  profit: null,
};
const today = () => `${new Date().toISOString().slice(0, 10)}T00:00:00.000Z`;
const STALE_ROUND_MS = 10 * 60 * 1000;
const EXPIRING_ACTION_MS = 30 * 60 * 1000;
const OPERATIONS_REFRESH_MS = 10_000;

function elapsed(value: string) {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

function moneyTotals(
  rows: CasinoProfitResponse["breakdown"],
  field: "grossGamingRevenue" | "returnedToPlayers" | "wagered",
) {
  const totals = new Map<string, bigint>();
  for (const row of rows) {
    totals.set(row.currency, (totals.get(row.currency) ?? 0n) + BigInt(row[field].amount));
  }
  return [...totals].map(([currency, amount]) => asAmount({ amount: String(amount), currency }));
}

function Metric({
  description,
  hint,
  label,
  onClick,
  tone,
  value,
}: {
  description: string;
  hint: string;
  label: string;
  onClick: () => void;
  tone: string;
  value: string;
}) {
  return (
    <button
      aria-label={`${label}: ${value}. ${description}`}
      className={`dashboard-metric dashboard-metric-${tone}`}
      onClick={onClick}
      title={description}
      type="button"
    >
      <span className="dashboard-metric-label">
        {label}{" "}
        <span aria-hidden="true" className="dashboard-help">
          ?
        </span>
      </span>
      <strong>{value}</strong>
      <small className="dashboard-metric-hint">
        {hint} <span className="dashboard-metric-link">View →</span>
      </small>
    </button>
  );
}

const title = (value: string) =>
  value
    .replace(/^admin\./, "")
    .replaceAll(/[._]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const ACTION_LABELS: Record<string, string> = {
  "admin.actions.list": "Approval queue viewed",
  "admin.audit.view": "Audit log viewed",
  "admin.login": "Signed in",
  "admin.player.list": "Player list viewed",
  "admin.reports.casino_profit.view": "Casino profit report viewed",
  "admin.round.search": "Round search",
  "admin.transactions.view": "Transactions viewed",
};

export function Dashboard({
  admin,
  navigate,
}: {
  admin: AuthenticatedAdmin;
  navigate: (
    screen: "actions" | "audit" | "blackjack" | "cases" | "poker" | "profit" | "rounds",
  ) => void;
}) {
  const [data, setData] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const refreshOperations = async () => {
      const [poker, blackjack] = await Promise.allSettled([
        api.pokerOperations(controller.signal, true),
        api.blackjackOperations(controller.signal, true),
      ]);
      if (!controller.signal.aborted) {
        setData((current) => ({
          ...current,
          blackjack: blackjack.status === "fulfilled" ? blackjack.value : current.blackjack,
          poker: poker.status === "fulfilled" ? poker.value : current.poker,
        }));
        if (poker.status === "fulfilled" || blackjack.status === "fulfilled")
          setUpdatedAt(new Date());
      }
    };
    Promise.allSettled([
      can(admin.role, "reports.financial.read")
        ? api.casinoProfit({ from: today() })
        : Promise.resolve(null),
      can(admin.role, "games.read")
        ? api.rounds({ signal: controller.signal, status: "open" })
        : Promise.resolve(null),
      can(admin.role, "audit.read")
        ? api.audit({ signal: controller.signal })
        : Promise.resolve(null),
      can(admin.role, "wallet.reversal.initiate") || can(admin.role, "wallet.reversal.approve")
        ? api.pendingActions(controller.signal)
        : Promise.resolve(null),
      can(admin.role, "games.read")
        ? api.pokerOperations(controller.signal)
        : Promise.resolve(null),
      can(admin.role, "games.read")
        ? api.blackjackOperations(controller.signal)
        : Promise.resolve(null),
      can(admin.role, "cases.read")
        ? api.caseQueue({ limit: 1, overdue: true, signal: controller.signal })
        : Promise.resolve(null),
    ])
      .then(([profit, openRounds, audit, actions, poker, blackjack, cases]) => {
        if (controller.signal.aborted) return;
        setData({
          actions: actions.status === "fulfilled" ? (actions.value?.actions ?? null) : null,
          audit: audit.status === "fulfilled" ? audit.value : null,
          blackjack: blackjack.status === "fulfilled" ? blackjack.value : null,
          cases: cases.status === "fulfilled" ? cases.value : null,
          openRounds: openRounds.status === "fulfilled" ? openRounds.value : null,
          poker: poker.status === "fulfilled" ? poker.value : null,
          profit: profit.status === "fulfilled" ? profit.value : null,
        });
        const failure = [profit, openRounds, audit, actions, poker, blackjack, cases].find(
          (result) => result.status === "rejected",
        );
        setError(
          failure?.status === "rejected"
            ? failure.reason instanceof ApiError
              ? failure.reason.message
              : "Some dashboard data could not be loaded."
            : null,
        );
        setUpdatedAt(new Date());
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    const interval = can(admin.role, "games.read")
      ? window.setInterval(() => void refreshOperations(), OPERATIONS_REFRESH_MS)
      : null;
    return () => {
      if (interval !== null) window.clearInterval(interval);
      controller.abort();
    };
  }, [admin.role]);

  const rows = data.profit?.breakdown ?? [];
  const roundCount = rows.reduce((total, row) => total + row.roundCount, 0);
  const approvalCount = data.actions?.length ?? 0;
  const auditHealthy = data.audit?.verified.intact ?? true;
  const staleRounds = data.openRounds?.rounds.filter(
    (round) => Date.now() - new Date(round.startedAt).getTime() >= STALE_ROUND_MS,
  );
  const expiringActions = data.actions?.filter(
    (action) => new Date(action.expiresAt).getTime() - Date.now() <= EXPIRING_ACTION_MS,
  );
  const activePokerAlerts = data.poker ? pokerAlerts(data.poker) : [];
  const activeBlackjackAlerts = data.blackjack ? blackjackAlerts(data.blackjack) : [];
  const overdueCases = data.cases?.total ?? 0;
  const dashboardHealthy =
    !error && auditHealthy && activePokerAlerts.length === 0 && activeBlackjackAlerts.length === 0;
  const attentionCount =
    Number((staleRounds?.length ?? 0) > 0) +
    Number((expiringActions?.length ?? 0) > 0) +
    Number(!auditHealthy) +
    Number(activePokerAlerts.length > 0) +
    Number(activeBlackjackAlerts.length > 0) +
    Number(overdueCases > 0) +
    Number(Boolean(error));

  if (loading) return <p className="empty">Loading dashboard…</p>;

  return (
    <section className="body dashboard">
      {error ? (
        <p className="banner" role="alert">
          {error}
        </p>
      ) : null}

      <div className="dashboard-context">
        <span>
          <strong>Today</strong> · 00:00–now · UTC
        </span>
        <span>Updated {updatedAt ? asMoment(updatedAt.toISOString()) : "just now"}</span>
      </div>

      <div className="dashboard-metrics">
        {data.profit ? (
          <>
            <Metric
              description="Gross gaming revenue is settled stakes minus player returns."
              hint="Settled stakes minus player returns"
              label="Today's GGR"
              onClick={() => navigate("profit")}
              tone="revenue"
              value={moneyTotals(rows, "grossGamingRevenue").join(" · ") || "No settled games"}
            />
            <Metric
              description="The total amount staked in games settled today."
              hint="Total settled stakes"
              label="Today's wagered"
              onClick={() => navigate("profit")}
              tone="wagered"
              value={moneyTotals(rows, "wagered").join(" · ") || "No settled games"}
            />
            <Metric
              description="Games completed and financially settled since 00:00 UTC."
              hint="Completed since 00:00 UTC"
              label="Rounds today"
              onClick={() => navigate("rounds")}
              tone="rounds"
              value={String(roundCount)}
            />
          </>
        ) : null}
        {data.actions ? (
          <Metric
            description="Sensitive actions waiting for review by a second operator."
            hint="Waiting for a second operator"
            label="Pending approvals"
            onClick={() => navigate("actions")}
            tone="approvals"
            value={String(approvalCount)}
          />
        ) : null}
      </div>

      <div className="dashboard-grid">
        <section className="card dashboard-health">
          <div className="card-bar">
            <span className="label-14-strong">System health</span>
            <span className={`dashboard-attention-count${dashboardHealthy ? "" : " has-alerts"}`}>
              {dashboardHealthy ? "Operational" : "Attention needed"}
            </span>
          </div>
          <div className="dashboard-health-items">
            <div>
              <span className={`dashboard-status-dot ${error ? "is-alert" : "is-ok"}`} />
              <span className="dashboard-health-copy">
                <strong>Backoffice connection</strong>
                <small>
                  {error ? "Some dashboard data is unavailable" : "Connected and receiving data"}
                </small>
              </span>
            </div>
            {data.audit ? (
              <div>
                <span className={`dashboard-status-dot ${auditHealthy ? "is-ok" : "is-alert"}`} />
                <span className="dashboard-health-copy">
                  <strong title="Confirms that recorded operator actions have not been altered.">
                    Operator record integrity
                  </strong>
                  <small>
                    {auditHealthy
                      ? `${data.audit.verified.checked} entries verified`
                      : "Chain verification failed"}
                  </small>
                </span>
              </div>
            ) : null}
            <div>
              <span className={`dashboard-status-dot ${error ? "is-alert" : "is-ok"}`} />
              <span className="dashboard-health-copy">
                <strong>Dashboard data</strong>
                <small>{error ? "Partial snapshot" : "Updated just now"}</small>
              </span>
            </div>
            {data.poker ? (
              <div>
                <span
                  className={`dashboard-status-dot ${activePokerAlerts.length ? "is-alert" : "is-ok"}`}
                />
                <span className="dashboard-health-copy">
                  <strong>Poker authority</strong>
                  <small>
                    {activePokerAlerts.length
                      ? activePokerAlerts[0]?.message
                      : data.poker.status === "disabled"
                        ? "Disabled in this environment"
                        : "Healthy"}
                  </small>
                </span>
              </div>
            ) : null}
            {data.blackjack ? (
              <div>
                <span
                  className={`dashboard-status-dot ${activeBlackjackAlerts.length ? "is-alert" : "is-ok"}`}
                />
                <span className="dashboard-health-copy">
                  <strong>Blackjack authority</strong>
                  <small>{activeBlackjackAlerts[0]?.message ?? "Healthy"}</small>
                </span>
              </div>
            ) : null}
          </div>
        </section>

        <section className="card dashboard-attention">
          <div className="card-bar">
            <span className="label-14-strong">Needs attention</span>
            <span className={`dashboard-attention-count${attentionCount ? " has-alerts" : ""}`}>
              {attentionCount
                ? `${attentionCount} ${attentionCount === 1 ? "item" : "items"}`
                : "All clear"}
            </span>
          </div>
          {error ? (
            <div className="dashboard-alert-row" role="status">
              <span>
                <span className="dashboard-severity severity-medium">Unknown</span>
                <strong>Dashboard data incomplete</strong>
              </span>
              <span>Use the available sections below</span>
            </div>
          ) : null}
          {attentionCount === 0 ? (
            <div className="dashboard-all-clear">
              <span aria-hidden="true" className="dashboard-all-clear-icon">
                ✓
              </span>
              <div>
                <strong>Operations look healthy</strong>
                <p className="dashboard-all-clear-copy">
                  No overdue cases, stale rounds, expiring approvals or integrity issues.
                </p>
              </div>
            </div>
          ) : null}
          {(staleRounds?.length ?? 0) > 0 ? (
            <button
              className="dashboard-alert-row"
              onClick={() => navigate("rounds")}
              type="button"
            >
              <span>
                <span className="dashboard-severity severity-high">High</span>
                <strong>{staleRounds?.length}</strong> rounds open over 10 minutes
              </span>
              <span>Investigate →</span>
            </button>
          ) : null}
          {overdueCases > 0 ? (
            <button className="dashboard-alert-row" onClick={() => navigate("cases")} type="button">
              <span>
                <span className="dashboard-severity severity-high">High</span>
                <strong>{overdueCases}</strong> overdue case{overdueCases === 1 ? "" : "s"}
              </span>
              <span>Open queue →</span>
            </button>
          ) : null}
          {(expiringActions?.length ?? 0) > 0 ? (
            <button
              className="dashboard-alert-row"
              onClick={() => navigate("actions")}
              type="button"
            >
              <span>
                <span className="dashboard-severity severity-medium">Medium</span>
                <strong>{expiringActions?.length}</strong> approvals expire within 30 minutes
              </span>
              <span>Open queue →</span>
            </button>
          ) : null}
          {data.audit && !auditHealthy ? (
            <button className="dashboard-alert-row" onClick={() => navigate("audit")} type="button">
              <span>
                <span className="dashboard-severity severity-critical">Critical</span>
                <strong>Operator record integrity failed</strong>
              </span>
              <span>Stop and investigate →</span>
            </button>
          ) : null}
          {activePokerAlerts.length > 0 ? (
            <button className="dashboard-alert-row" onClick={() => navigate("poker")} type="button">
              <span>
                <span className={`dashboard-severity severity-${activePokerAlerts[0]?.severity}`}>
                  {activePokerAlerts[0]?.severity === "critical" ? "Critical" : "High"}
                </span>
                <strong>{activePokerAlerts.length}</strong> Poker operational alert
                {activePokerAlerts.length === 1 ? "" : "s"}
              </span>
              <span>Investigate →</span>
            </button>
          ) : null}
          {activeBlackjackAlerts.length > 0 ? (
            <button
              className="dashboard-alert-row"
              onClick={() => navigate("blackjack")}
              type="button"
            >
              <span>
                <span
                  className={`dashboard-severity severity-${activeBlackjackAlerts[0]?.severity}`}
                >
                  {activeBlackjackAlerts[0]?.severity === "critical" ? "Critical" : "High"}
                </span>
                <strong>{activeBlackjackAlerts.length}</strong> Blackjack operational alert
                {activeBlackjackAlerts.length === 1 ? "" : "s"}
              </span>
              <span>Investigate →</span>
            </button>
          ) : null}
        </section>

        {data.openRounds ? (
          <section className="card dashboard-live-games">
            <div className="card-bar">
              <span className="label-14-strong">Live games</span>
              <button className="link-button" onClick={() => navigate("rounds")} type="button">
                All rounds →
              </button>
            </div>
            {data.openRounds.rounds.length ? (
              data.openRounds.rounds.slice(0, 5).map((round) => (
                <button
                  className="dashboard-live-row"
                  key={round.id}
                  onClick={() => navigate("rounds")}
                  type="button"
                >
                  <span className="dashboard-live-copy">
                    <strong>{title(round.game)}</strong>
                    <small>{round.tableId ?? "No table"}</small>
                  </span>
                  <span className="dashboard-live-copy">
                    <strong>
                      {round.players.length} player{round.players.length === 1 ? "" : "s"}
                    </strong>
                    <small
                      className={
                        Date.now() - new Date(round.startedAt).getTime() >= STALE_ROUND_MS
                          ? "is-stale"
                          : ""
                      }
                    >
                      {elapsed(round.startedAt)} open
                    </small>
                  </span>
                </button>
              ))
            ) : (
              <p className="empty">No games are currently in progress.</p>
            )}
          </section>
        ) : null}

        {data.profit ? (
          <section className="card dashboard-games">
            <div className="card-bar">
              <span className="label-14-strong">Games today</span>
              <button className="link-button" onClick={() => navigate("profit")} type="button">
                Full report →
              </button>
            </div>
            {rows.length ? (
              <table className="mobile-cards">
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Rounds</th>
                    <th>GGR</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.game}-${row.tableId}-${row.currency}`}>
                      <td className="label-13 dashboard-game-name" data-label="Game">
                        {row.game}
                      </td>
                      <td className="mono-12" data-label="Rounds">
                        {row.roundCount}
                      </td>
                      <td className="mono-12" data-label="GGR">
                        {asAmount(row.grossGamingRevenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="empty">No games have settled today.</p>
            )}
          </section>
        ) : null}

        {data.audit ? (
          <section className="card dashboard-activity">
            <div className="card-bar">
              <span className="label-14-strong">Recent operator activity</span>
              <button className="link-button" onClick={() => navigate("audit")} type="button">
                Audit log →
              </button>
            </div>
            {data.audit.entries.slice(0, 5).map((entry) => (
              <div className="dashboard-activity-row" key={entry.id}>
                <span className="dashboard-activity-copy">
                  <strong>{ACTION_LABELS[entry.actionType] ?? title(entry.actionType)}</strong>
                  <small>{title(entry.role)}</small>
                </span>
                <time dateTime={entry.createdAt}>{asMoment(entry.createdAt)}</time>
              </div>
            ))}
          </section>
        ) : null}
      </div>
    </section>
  );
}
